import { useEffect, useState } from 'react';

import analytics from '../../lib/analytics';

import type { ProductCatalogItem } from '../../../../types/product-catalog';
import type { useProductCatalogShell } from './use-product-catalog-shell';

const CHILIPIPER_SCRIPT_SRC = 'https://avalara.chilipiper.com/concierge-js/cjs/concierge.js';

type ShellUser = ReturnType<typeof useProductCatalogShell>['user'];
type ShellCustomerAccountId = ReturnType<typeof useProductCatalogShell>['customerAccountId'];

// Shared between containers/product-catalog/index.tsx (the catalog grid) and
// containers/product-catalog/product-detail.tsx (a single product's page) — both load the same
// ChiliPiper concierge script and drive the same "Contact Sales" scheduling dialog, just from
// different entry points (a catalog card vs. a pricing tier's Contact Sales CTA).
export const useChiliPiperContact = (
  shouldLoadScript: boolean,
  customerAccountId: ShellCustomerAccountId,
  user: ShellUser,
) => {
  const [contactDialogProduct, setContactDialogProduct] = useState<ProductCatalogItem | null>(null);
  const [schedulingInProgress, setSchedulingInProgress] = useState(false);
  const [contactError, setContactError] = useState(false);

  useEffect(() => {
    if (!shouldLoadScript) return;
    if (window.ChiliPiper !== undefined) return;
    // window.ChiliPiper stays undefined for the whole loading window of the (deferred) script, so
    // an unmount/remount in that window would otherwise see it still undefined and inject a second
    // copy — check for an already-injected script tag too, not just the resolved global.
    if (document.querySelector(`script[src="${CHILIPIPER_SCRIPT_SRC}"]`)) return;

    const script = document.createElement('script');
    script.src = CHILIPIPER_SCRIPT_SRC;
    script.defer = true;
    script.onerror = () => {
      analytics.track('ChiliPiper concierge script failed to load');
    };

    document.body.appendChild(script);
  }, [shouldLoadScript]);

  const handleContactClick = (item: ProductCatalogItem) => {
    setContactDialogProduct(item);
    setContactError(false);

    if (typeof window.ChiliPiper?.submit !== 'function') {
      analytics.track('ChiliPiper is undefined');
      setContactError(true);
      return;
    }

    setSchedulingInProgress(true);

    try {
      window.ChiliPiper.submit('avalara', 'us-sdr-concierge', {
        map: true,
        domElement: '#product-catalog-chilipiper-calendar',
        trigger: 'InAppButton',
        accountId: customerAccountId,
        lead: {
          PersonEmail: user?.email,
          PersonFirstName: user?.firstName,
          PersonLastName: user?.lastName,
          ProductName: item.title,
        },
        onError: () => {
          setSchedulingInProgress(false);
          setContactError(true);
        },
        onSuccess: () => {
          setSchedulingInProgress(false);
        },
        onClose: () => {
          setContactDialogProduct(null);
          setSchedulingInProgress(false);
        },
      });
    } catch {
      // ChiliPiper's own async failures are caught by onError above; this guards against a
      // synchronous throw from submit() itself (e.g. a partially-initialized script), which
      // would otherwise leave schedulingInProgress stuck true with no error shown.
      analytics.track('ChiliPiper submit threw synchronously');
      setSchedulingInProgress(false);
      setContactError(true);
    }
  };

  const handleDismissDialog = () => {
    setContactDialogProduct(null);
    setSchedulingInProgress(false);
    setContactError(false);
  };

  return { contactDialogProduct, schedulingInProgress, contactError, handleContactClick, handleDismissDialog };
};
